import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MessageTemplateStatusTable from '../../../components/tables/MessageTemplateStatusTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMessageTemplateStatusApi } from '../../../hooks/useMessageTemplateStatusApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { MessageTemplateStatusDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const MessageTemplateStatusList: React.FC = () => {
  const { search, remove, isLoading, error } = useMessageTemplateStatusApi();
  const [items, setItems] = useState<MessageTemplateStatusDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Message Template Statuses', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Message Template Statuses:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: MessageTemplateStatusDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting MessageTemplateStatus:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/messagetemplatestatuses"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.MessageTemplateStatus.View"
    >
      <Card
        title="Manage Message Template Statuses"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/messagetemplatestatuses/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <MessageTemplateStatusTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/messagetemplatestatuses/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default MessageTemplateStatusList;
